import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '@/assets/app/css/record.module.css';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import useAppStore from '@/shared/store/UseAppStore';
import ImgHeaderLevel from '@/assets/app/images/common/header/header-level.png';
import ImgHeaderStar from '@/assets/app/images/common/header/header-star.png';
import ImgHeaderCalendar from '@/assets/app/images/common/header/header-calendar.png';
import logger from '@/shared/utils/Logger';
import { vAppAjax } from '@/shared/utils/CommonUtil';

interface HistoryItemProps {
  dateLabel: string;
  title: string;
  detail?: string;
  detailColor?: string;
  isSuccess?: boolean;
  bonus?: string[];
}

interface PointDetail {
  eventTypeCode: string;
  points: number;
  description: string;
}

interface HistoryGroup {
  sessionId: number;
  label: string; // "고려시대/강감찬/챕터2"
  createdAt: string;
  dateDisplay: string; // "1일전"
  details: PointDetail[];
}

interface HistoryResponse {
  recent: HistoryGroup | null;
  previous: HistoryGroup[];
}

const HistoryItem: React.FC<{ group: HistoryGroup }> = ({ group }) => (
  <>
    <div className="mb-4">
      <p className="text-xs text-gray-400 mb-1">{group.dateDisplay}</p>
      <h3 className="font-semibold text-gray-700">{group.label}</h3>
      <div className="space-y-0.5 mt-1">
        {group.details.map((detail, idx) => (
          <p key={idx} className="text-[11px] text-cyan-500">
            {detail.description}
          </p>
        ))}
      </div>
    </div>
    <hr style={{ color: '#F2F2F2' }} />
  </>
);

const Record: React.FC = () => {
  const navigate = useNavigate();
  const { setIsShowBottomNavigation, setIsShowProfileHeader } = useAppStore();
  const { member } = useAppAuthStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HistoryResponse>();

  const loadData = async () => {
    await vAppAjax(
      'get',
      `my/record`,
      {},
      (response: AxiosResponse<CommonResult>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          console.log;
          setData(response.data.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(true);
    setIsShowProfileHeader(false);
    loadData();
  }, []);

  const stats = {
    level: 1,
    stars: 30,
    consecutiveDays: 12,
    answeredCount: 3,
    correctCount: 1,
  };

  return (
    <div className="page-container page-container--white" style={{ padding: '20px' }}>
      <div style={{ textAlign: 'center' }}>
        <span style={{ color: '#4B4B4B', fontSize: 24, fontWeight: 800 }}>기록</span>
      </div>

      <div
        className={styles['mypage__stats']}
        style={{
          width: '100%',
          display: 'flex',
          gap: 10,
          marginTop: 10,
          justifyContent: 'space-between',
        }}
      >
        <div
          className="flex"
          style={{
            width: 96,
            height: 96,
            border: '1px solid #E5E5E5',
            borderRadius: 14,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <span>
            <img src={ImgHeaderLevel} alt="level" />
          </span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>Lv.{member?.level}</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: '#8D8D8D' }}>나의 레벨</span>
        </div>
        <div
          className="flex"
          style={{
            width: 96,
            height: 96,
            border: '1px solid #E5E5E5',
            borderRadius: 14,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <span>
            <img src={ImgHeaderStar} alt="level" />
          </span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{member?.totalPoints}</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: '#8D8D8D' }}>별포인트</span>
        </div>
        <div
          className="flex"
          style={{
            width: 96,
            height: 96,
            border: '1px solid #E5E5E5',
            borderRadius: 14,
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <span>
            <img src={ImgHeaderCalendar} alt="level" />
          </span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{member?.consecutiveDays}일차</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: '#8D8D8D' }}>연속학습</span>
        </div>
      </div>

      <div className="flex w-full items-center justify-center border border-[#e5e5e5] rounded-[14px] w-[320px] h-[67px] mt-3 mx-auto">
        {/* record__stat-item */}
        <div className="flex-1 text-center">
          <div className="text-[16px] font-extrabold text-[#4b4b4b]">0</div>
          <div className="text-[11px] text-[#8d8d8d] mt-1">완료한 인물</div>
        </div>

        {/* record__divider */}
        <div className="w-[0.5px] h-[42px] bg-[#efefef]"></div>

        {/* record__stat-item */}
        <div className="flex-1 text-center">
          <div className="text-[16px] font-extrabold text-[#4b4b4b]">0</div>
          <div className="text-[11px] text-[#8d8d8d] mt-1">완료한 시대</div>
        </div>
      </div>

      {data?.recent && (
        <section className="mt-6 mb-4">
          <h2 className="text-xl font-bold mb-2 text-gray-800">최근</h2>
          <hr style={{ color: '#F2F2F2' }} />
          <div className="py-4">
            <HistoryItem group={data.recent} />
          </div>
        </section>
      )}

      {data?.previous && data.previous.length > 0 && (
        <section>
          <h2 className="text-xl font-bold mb-4 text-gray-800">이전</h2>
          <div className="space-y-4">
            {data?.previous.map(group => <HistoryItem key={group.sessionId} group={group} />)}
          </div>
        </section>
      )}

      {/* <div className={styles['record__quick-stats']}>
        <h2 className={styles['record__section-title']}>최근</h2>
        <div className={styles['record__quick-stats-content']}>
          <p className="text-secondary">
            고과서/역장힙/밴드2 <span className="text-primary-color">쿨 넥상 해당 1입 세바</span>
          </p>
          <div className={styles['record__quick-numbers']}>
            <div>
              <div className="styles['record__stat-item']__value">{stats.answeredCount}</div>
              <div className="text-secondary">하루로 연속</div>
            </div>
            <div>
              <div className="styles['record__stat-item']__value">{stats.correctCount}</div>
              <div className="text-secondary">문항로 사례</div>
            </div>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h3 className="section-header__title">이전</h3>
          <button className="section-header__action">전체보기</button>
        </div>

        <div className={styles['record__history']}>
          {historyItems.map((item, index) => (
            <div key={index} className={styles['record__history-item']}>
              <p className={styles['record__history-date']}>{item.date}</p>
              <p className="text-primary font-semibold mb-sm">{item.type}</p>
              {item.items.map((subItem, subIndex) => (
                <p key={subIndex} className={styles['record__history-subitem']}>
                  {subItem}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default Record;
